import api from './api';

export interface User {
  id: number;
  email: string;
  role: 'ADMIN' | 'TECHNICIAN' | 'PM';
  name?: string;
  tenantId?: number;
  tenantName?: string;
  tenantSubdomain?: string | null;
  /** Set when tenant has own backend (e.g. client's PC); frontend uses this for API base URL */
  apiBaseUrl?: string | null;
  /** Platform owner — can provision new companies (Companies screen) */
  isSuperAdmin?: boolean;
  /** Technician list only: invite lifecycle state */
  status?: 'invited' | 'invite_expired' | 'active';
}

export interface LoginResponse {
  token: string;
  user: User;
  tenant?: { tenantId: number; tenantName: string; tenantSubdomain?: string | null; apiBaseUrl?: string | null };
}

export interface TenantChoice {
  id: number;
  name: string;
}

export const authAPI = {
  login: async (email: string, password: string, tenantId?: number): Promise<LoginResponse> => {
    const body: { email: string; password: string; tenantId?: number } = { email, password };
    if (tenantId != null) body.tenantId = tenantId;
    const response = await api.post<LoginResponse>('/auth/login', body);
    return response.data;
  },

  getMe: async (): Promise<User> => {
    const response = await api.get<User>('/auth/me');
    return response.data;
  },

  /** No password → the technician gets a set-password invite email. */
  createTechnician: async (email: string, name: string, password?: string): Promise<User & { emailSent?: boolean; message?: string }> => {
    const body: { email: string; name: string; password?: string } = { email, name };
    if (password) body.password = password;
    const response = await api.post<User & { emailSent?: boolean; message?: string }>('/auth/technicians', body);
    return response.data;
  },

  resendTechnicianInvite: async (id: number): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(`/auth/technicians/${id}/resend-invite`);
    return response.data;
  },

  listTechnicians: async (): Promise<User[]> => {
    const response = await api.get<User[]>('/auth/technicians');
    return response.data;
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await api.put('/auth/me/password', { currentPassword, newPassword });
  },

  updateTechnician: async (id: number, data: { email?: string; name?: string; password?: string }): Promise<User> => {
    const response = await api.put<User>(`/auth/technicians/${id}`, data);
    return response.data;
  },

  deleteTechnician: async (id: number): Promise<void> => {
    await api.delete(`/auth/technicians/${id}`);
  },

  forgotPassword: async (email: string, tenantId?: number): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/auth/forgot-password', { email, tenantId });
    return response.data;
  },

  resetPassword: async (token: string, newPassword: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/auth/reset-password', { token, newPassword });
    return response.data;
  },
};

